/** Kiosk entry decision. Pure; dates are business-local `YYYY-MM-DD`, times `HH:MM` (rule 3). */

export interface BranchScope {
  mode: 'one' | 'chosen' | 'all';
  homeBranchId: string;
  branchIds?: string[];
  /** Max visits at non-home branches. undefined = unlimited. */
  nonHomeVisitLimit?: number;
}

export interface Plan {
  id: string;
  kind: string;
  startDate: string;
  endDate: string;
  status: 'active' | 'frozen' | 'expired' | 'cancelled';
  branchScope: BranchScope;
  sessionsLeft?: number;
}

export interface EntryMember {
  id: string;
  /** Outstanding balance in integer paise. */
  balanceDuePaise?: number;
}

export interface BranchHours {
  open: string; // HH:MM
  close: string; // HH:MM, exclusive; close < open means past midnight
}

export interface EntrySettings {
  enforceBranchHours: boolean;
  /** Amber when the plan ends within this many days (inclusive). */
  expiryWarnDays: number;
  warnOnBalanceDue: boolean;
}

export interface DecideInput {
  member: EntryMember;
  plans: Plan[];
  branchId: string;
  onDate: string;
  nowLocalTime: string;
  /** planId -> prior visits made at non-home branches (counted by the caller). */
  visitCountsAtBranch: Record<string, number>;
  branchHours: BranchHours | null;
  settings: EntrySettings;
}

export type DenyReason =
  | 'no_active_plan' | 'expired' | 'frozen' | 'wrong_branch' | 'visit_limit_hit' | 'outside_hours' | 'not_started' | 'no_sessions_left';
export type WarnReason = 'expires_soon' | 'balance_due';

export interface Decision {
  result: 'allow' | 'deny';
  colour: 'green' | 'red' | 'amber';
  reasons: string[];
  plan?: string;
}

/** Whole days from a to b, both YYYY-MM-DD, pure calendar maths (no timezone involved). */
export function daysBetween(a: string, b: string): number {
  const p = (s: string) => {
    const [y, m, d] = s.split('-').map(Number) as [number, number, number];
    return Date.UTC(y, m - 1, d);
  };
  return Math.round((p(b) - p(a)) / 86_400_000);
}

export function withinHours(time: string, hours: BranchHours): boolean {
  const { open, close } = hours;
  if (open === close) return true; // 24h
  return open < close ? time >= open && time < close : time >= open || time < close;
}

function evalPlan(plan: Plan, i: DecideInput): { denies: DenyReason[]; warns: WarnReason[] } {
  const denies: DenyReason[] = [];
  if (plan.status === 'frozen') denies.push('frozen');
  else if (plan.status === 'expired' || plan.status === 'cancelled' || plan.endDate < i.onDate) denies.push('expired');
  else if (plan.status !== 'active') denies.push('no_active_plan'); // unknown status from stored data: fail closed
  else if (plan.startDate > i.onDate) denies.push('not_started');
  if (denies.length === 0 && plan.sessionsLeft !== undefined && plan.sessionsLeft <= 0) denies.push('no_sessions_left');

  const s = plan.branchScope;
  const home = i.branchId === s.homeBranchId;
  let branchOk = home;
  if (!home) {
    if (s.mode === 'all') branchOk = true;
    else if (s.mode === 'chosen') branchOk = (s.branchIds ?? []).includes(i.branchId);
  }
  if (!branchOk) denies.push('wrong_branch');
  else if (!home && s.nonHomeVisitLimit !== undefined && (i.visitCountsAtBranch[plan.id] ?? 0) >= s.nonHomeVisitLimit) {
    denies.push('visit_limit_hit');
  }

  const warns: WarnReason[] = [];
  if (denies.length === 0) {
    const left = daysBetween(i.onDate, plan.endDate);
    if (left <= i.settings.expiryWarnDays) warns.push('expires_soon');
  }
  return { denies, warns };
}

export function decideEntry(input: DecideInput): Decision {
  const { member, plans, settings } = input;

  if (settings.enforceBranchHours && input.branchHours && !withinHours(input.nowLocalTime, input.branchHours)) {
    return { result: 'deny', colour: 'red', reasons: ['outside_hours'] };
  }
  if (plans.length === 0) return { result: 'deny', colour: 'red', reasons: ['no_active_plan'] };

  const evals = plans.map((p) => ({ p, ...evalPlan(p, input) }));
  const ok = evals.filter((e) => e.denies.length === 0);
  if (ok.length > 0) {
    // Prefer a plan with no warning, then the one ending latest; ties keep input order.
    ok.sort((a, b) => a.warns.length - b.warns.length || (a.p.endDate < b.p.endDate ? 1 : a.p.endDate > b.p.endDate ? -1 : 0));
    const best = ok[0]!;
    const warns: string[] = [...best.warns];
    if (settings.warnOnBalanceDue && (member.balanceDuePaise ?? 0) > 0) warns.push('balance_due');
    return { result: 'allow', colour: warns.length ? 'amber' : 'green', reasons: warns, plan: best.p.id };
  }
  const reasons = [...new Set(evals.flatMap((e) => e.denies))];
  return { result: 'deny', colour: 'red', reasons };
}
